import { useTheme } from "@/context/ThemeContext";
import { Moon, Sun } from "lucide-react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NotificationsBell } from "@/components/NotificationsBell";

export function BrandMark({ size = 32 }) {
  return (
    <div className="flex items-center gap-2">
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="CLING logo">
        <path d="M30 8a14 14 0 1 0 0 24" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" className="text-[color:hsl(var(--teal))]" />
        <circle cx="16" cy="15" r="3" fill="currentColor" className="text-[color:hsl(var(--amber))]" />
        <circle cx="24" cy="22" r="3" fill="currentColor" className="text-[color:hsl(var(--teal))]" />
        <line x1="17" y1="17" x2="23" y2="20" stroke="currentColor" strokeWidth="1.6" className="text-[color:hsl(var(--teal))]" />
      </svg>
      <span className="font-display font-extrabold text-xl tracking-tight">CLING</span>
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  const link = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-[color:hsl(var(--teal))]" : "text-muted-foreground hover:text-foreground"}`;

  return (
    <header className="sticky top-0 z-40 border-b bg-background/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to={user ? "/home" : "/"} data-testid="nav-logo" className="flex items-center">
          <BrandMark />
        </Link>

        {user ? (
          <nav className="hidden md:flex items-center gap-6">
            <NavLink to="/home" className={link} data-testid="nav-home">Home</NavLink>
            <NavLink to="/rooms" className={link} data-testid="nav-rooms">Rooms</NavLink>
            <NavLink to="/knowledge" className={link} data-testid="nav-knowledge">Knowledge</NavLink>
            <NavLink to="/helper-pro" className={link} data-testid="nav-helper-pro">Helper Pro</NavLink>
            <NavLink to="/earnings" className={link} data-testid="nav-earnings">Earnings</NavLink>
            <NavLink to={`/u/${user.user_id}`} className={link} data-testid="nav-profile">Profile</NavLink>
            {user.role === "admin" && <NavLink to="/admin" className={link} data-testid="nav-admin">Admin</NavLink>}
          </nav>
        ) : (
          <nav className="hidden md:flex items-center gap-6">
            <a href="#how" className="text-sm font-medium text-muted-foreground hover:text-foreground">How it works</a>
            <a href="#principles" className="text-sm font-medium text-muted-foreground hover:text-foreground">Principles</a>
          </nav>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="p-2 rounded-full hover:bg-secondary transition"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user ? (
            <>
              <NotificationsBell />
              <Avatar className="h-8 w-8 border">
                <AvatarImage src={user.picture} />
                <AvatarFallback className="text-xs bg-[color:hsl(var(--teal))] text-white">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout">Sign out</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => nav("/login")} data-testid="nav-login">Sign in</Button>
              <Button className="btn-teal" size="sm" onClick={() => nav("/signup")} data-testid="nav-signup">Get started</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
